import React from "react";
import { useShallow } from "zustand/react/shallow";
import { GameRotation } from "@uno/protocol";
import useStoreRoom from "store/useStoreRoom";

const ARROW_SIZE = 24;

const DiscardRotationIndicator = React.forwardRef<HTMLDivElement>((props, ref: React.Ref<HTMLDivElement>) => {
    const room = useStoreRoom(useShallow(state => ({ rotation: state.room?.game?.rotation, color: state.room?.game?.color })));
    const [dimensions, setDimensions] = React.useState({ width: 0, height: 0, borderWidth: 0 });
    const [rotationChanged, setRotationChanged] = React.useState(false);
    const { width, height, borderWidth } = dimensions;

    React.useEffect(() => {
        if(!ref || !("current" in ref) || !ref.current) {
            return;
        }

        const updateDimensions = () => {
            const element = (ref as React.RefObject<HTMLDivElement>).current;

            if(!element) {
                return;
            }

            const { width, height } = element.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(element);
            const bw = parseFloat(computedStyle.borderWidth) || 0;

            setDimensions({ width, height, borderWidth: bw });
        };

        updateDimensions();

        const observer = new ResizeObserver(updateDimensions);
        observer.observe(ref.current);

        return () => observer.disconnect();
    }, [ref]);

    React.useEffect(() => {
        setRotationChanged(true);
        const timeout = setTimeout(() => setRotationChanged(false), 800);

        return () => clearTimeout(timeout);
    }, [room?.rotation]);

    const isClockwise = room?.rotation === GameRotation.Clockwise;
    const color = `var(--${room?.color?.toLowerCase()}-fill-color, #333)`;
    const cx = (width / 2) - (borderWidth / 2);
    const cy = (height / 2) - (borderWidth / 2);
    const radius = Math.min(width - borderWidth, height - borderWidth) / 2;

    const arrow = (
        <svg className="absolute w-6 h-6" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: -(ARROW_SIZE / 2), marginTop: -(ARROW_SIZE / 2) }}>
            <path fill={color} d="M24 11.305l-7.997 11.39L8 11.305z" />
        </svg>
    );

    return (
        <div
            key={`${isClockwise}`}
            className="absolute inset-0 will-change-transform"
            style={{
                animation: `discardSpin ${rotationChanged ? "0.8s" : "10s"} ${isClockwise ? "normal" : "reverse"} linear infinite`,
            }}
        >
            <div className="absolute" style={{ left: cx + radius, top: cy, transform: `rotate(${isClockwise ? 0 : 180}deg)` }}>
                {arrow}
            </div>
            <div className="absolute" style={{ left: cx - radius, top: cy, transform: `rotate(${isClockwise ? 180 : 0}deg)` }}>
                {arrow}
            </div>
        </div>
    );
});

export default React.memo(DiscardRotationIndicator);
